import { create } from 'zustand';
import { isFractionalQtyLine } from '@shared/quantity';
import type { Product } from '../../catalog/domain-api'
import type { SelectedMenuOption } from '../../catalog/domain-api';
import { menuOptionsKey, optionsPriceDelta, unitPriceWithOptions } from '../../catalog/domain-api';
import { getConsolidateCartLines } from '../../shared/pos/posCartSettings';

export type CartItem = {
  lineId: string;
  productId: string;
  variantId?: string | null;
  name: string;
  sku?: string;
  category: Product['categoryId'];
  brandId?: string | null;
  price: number;
  quantity: number;
  itemType: 'product' | 'custom' | 'package';
  /** itemType 'package': id de public.service_packages (productId lleva el mismo uuid). */
  packageId?: string;
  catalogItemType?: Product['itemType'];
  affectsStock: boolean;
  unitOfMeasure?: Product['unitOfMeasure'];
  sellByWeight?: boolean;
  /** QSR menu modifiers snapshot */
  menuOptions?: SelectedMenuOption[];
  lineNote?: string | null;
  /** Comisión: quién realizó el servicio (para atribución de comisiones) */
  performedByUserId?: string | null;
  /** Comisión: quién vendió el item (fallback si no hay performed_by) */
  soldByUserId?: string | null;
  /** Ya salió en una comanda de cocina (no reenviar en Mandar ronda). */
  kitchenSent?: boolean;
  /** Comanda que ya tiene esta línea (fire-before-pay de mostrador): se attachea a la venta al cobrar. */
  kitchenTicketId?: string | null;
};

export type AddProductToCartOptions = {
  quantity?: number;
  variantId?: string | null;
  menuOptions?: SelectedMenuOption[];
  lineNote?: string | null;
  /** Override catalog price (e.g. OT unitPrice). */
  unitPrice?: number;
  /** Comisión: vendedor por defecto de la línea, normalmente el usuario actual del POS. */
  soldByUserId?: string | null;
};

type CustomSaleInput = {
  name: string;
  amount: number;
  /** Producto `is_misc` de la org (reemplaza el id global `varios`). */
  miscProductId: string;
};

/** Paquete vendible en POS (snapshot de public.service_packages al agregar). */
export type PackageCartInput = {
  packageId: string;
  name: string;
  price: number;
};

function createCartLineId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

type PosState = {
  selectedCategory: Product['categoryId'] | 'all';
  cart: CartItem[];
  setCategory: (category: PosState['selectedCategory']) => void;
  addProductToCart: (product: Product, quantityOrOpts?: number | AddProductToCartOptions) => void;
  addCustomItemToCart: (item: CustomSaleInput) => void;
  addPackageToCart: (pkg: PackageCartInput, quantity?: number) => void;
  incrementCartItem: (lineId: string) => void;
  removeFromCart: (lineId: string) => void;
  setCartLineQuantity: (lineId: string, quantity: number) => void;
  removeCartLine: (lineId: string) => void;
  /** Gap 3: editar la nota de una línea existente. Trim; vacío → null. */
  setCartLineNote: (lineId: string, note: string | null) => void;
  /** Gap 4: re-editar menu options de una línea existente. Recalcula price. */
  updateCartLineMenuOptions: (lineId: string, menuOptions: SelectedMenuOption[] | undefined, lineNote?: string | null) => void;
  /** Gap 4: re-editar variante de una línea existente. Recalcula price/name/sku. */
  updateCartLineVariant: (lineId: string, variantId: string | null, name: string, price: number, sku?: string | null) => void;
  /** Gap 5: editar nombre/monto de una venta Varios (custom) después de agregada. */
  updateCustomCartLine: (lineId: string, name: string, amount: number) => void;
  /** Drift de catálogo al cobrar: pisa el precio congelado de la línea con el actual. */
  setCartLinePrice: (lineId: string, price: number) => void;
  clearCart: () => void;
  replaceCart: (cart: CartItem[]) => void;
  /** Comisión: asignar quién realizó/vendió un item del carrito */
  setCartLineAttribution: (lineId: string, performedByUserId: string | null, soldByUserId: string | null) => void;
};

export function isFractionalCartLine(
  item: Pick<CartItem, 'unitOfMeasure'>,
): boolean {
  return isFractionalQtyLine(item);
}

function getCartLineQuantityDelta(
  item: Pick<CartItem, 'unitOfMeasure'>,
  direction: 1 | -1,
): number {
  if (!isFractionalCartLine(item)) {
    return direction;
  }
  if (item.unitOfMeasure === 'hour') {
    return direction * 0.25;
  }
  // kg/g/l/ml/m/cm — small fractional step
  return direction * 0.1;
}

export const usePosStore = create<PosState>((set) => ({
  selectedCategory: 'all',
  cart: [],
  setCategory: (category) => set({ selectedCategory: category }),
  addProductToCart: (product, quantityOrOpts = 1) =>
    set((state) => {
      const opts: AddProductToCartOptions =
        typeof quantityOrOpts === 'number' ? { quantity: quantityOrOpts } : quantityOrOpts ?? {};
      const quantity = opts.quantity ?? 1;
      const menuOptions = opts.menuOptions?.length ? opts.menuOptions : undefined;
      const variantId = opts.variantId ?? null;
      const lineNote = opts.lineNote?.trim() || null;
      const optionsKey = menuOptionsKey(menuOptions);
      const noteKey = lineNote ?? '';
      const variantKey = variantId ?? '';
      const unitPrice =
        opts.unitPrice != null && Number.isFinite(opts.unitPrice)
          ? opts.unitPrice
          : unitPriceWithOptions(product.price, menuOptions);
      // Gap 6: merge condicional de items repetidos (unit + fraccional).
      // Default true (alineado con Square/Toast). Si la sucursal lo apagó, siempre crea línea nueva.
      // Merge key: productId + itemType + variantId + menuOptionsKey + lineNote + price (centavos).
      // No mergear si precio distinto (lista de precio, tier, OT override, sync de catálogo).
      const consolidate = getConsolidateCartLines();
      const priceCents = Math.round(unitPrice * 100);
      const existing = consolidate
        ? state.cart.find(
            (item) =>
              item.productId === product.id &&
              item.itemType === 'product' &&
              (item.variantId ?? '') === variantKey &&
              menuOptionsKey(item.menuOptions) === optionsKey &&
              (item.lineNote?.trim() || '') === noteKey &&
              Math.round(item.price * 100) === priceCents,
          )
        : undefined;
      const affectsStock = product.itemType !== 'service' && product.isMisc !== true;
      const qty = Math.max(0.001, quantity);
      if (existing) {
        return {
          cart: state.cart.map((item) =>
            item.lineId === existing.lineId ? { ...item, quantity: item.quantity + qty } : item,
          ),
        };
      }
      return {
        cart: [
          ...state.cart,
          {
            lineId: createCartLineId(),
            productId: product.id,
            variantId,
            name: product.name,
            sku: product.sku,
            category: product.categoryId,
            brandId: product.brandId ?? null,
            price: unitPrice,
            quantity: qty,
            itemType: 'product',
            catalogItemType: product.itemType === 'service' ? 'service' : 'physical',
            menuOptions,
            lineNote,
            performedByUserId: null,
            soldByUserId: opts.soldByUserId ?? null,
            affectsStock,
            unitOfMeasure: product.unitOfMeasure,
            sellByWeight: product.sellByWeight,
          },
        ],
      };
    }),
  addCustomItemToCart: ({ name, amount, miscProductId }) =>
    set((state) => ({
      cart: [
        ...state.cart,
        {
          lineId: createCartLineId(),
          productId: miscProductId,
          name,
          sku: 'VARIOS',
          category: '',
          price: amount,
          quantity: 1,
          itemType: 'custom',
          affectsStock: false,
        },
      ],
    })),
  addPackageToCart: (pkg, quantity = 1) =>
    set((state) => {
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));
      const priceCents = Math.round(pkg.price * 100);
      // Merge determinístico por paquete + precio (snapshot). Sin variantes ni opciones.
      const existing = getConsolidateCartLines()
        ? state.cart.find(
            (item) =>
              item.itemType === 'package' &&
              item.packageId === pkg.packageId &&
              Math.round(item.price * 100) === priceCents,
          )
        : undefined;
      if (existing) {
        return {
          cart: state.cart.map((item) =>
            item.lineId === existing.lineId ? { ...item, quantity: item.quantity + qty } : item,
          ),
        };
      }
      return {
        cart: [
          ...state.cart,
          {
            lineId: createCartLineId(),
            productId: pkg.packageId,
            packageId: pkg.packageId,
            name: pkg.name,
            category: '',
            price: pkg.price,
            quantity: qty,
            itemType: 'package' as const,
            affectsStock: false,
          },
        ],
      };
    }),
  incrementCartItem: (lineId) =>
    set((state) => ({
      cart: state.cart.map((item) =>
        item.lineId === lineId
          ? { ...item, quantity: item.quantity + getCartLineQuantityDelta(item, 1) }
          : item,
      ),
    })),
  removeFromCart: (lineId) =>
    set((state) => ({
      cart: state.cart
        .map((item) =>
          item.lineId === lineId
            ? { ...item, quantity: item.quantity + getCartLineQuantityDelta(item, -1) }
            : item,
        )
        .filter((item) =>
          isFractionalCartLine(item) ? item.quantity >= 0.001 : item.quantity > 0,
        ),
    })),
  setCartLineQuantity: (lineId, quantity) =>
    set((state) => {
      const line = state.cart.find((item) => item.lineId === lineId);
      if (!line) {
        return state;
      }
      if (isFractionalCartLine(line)) {
        const q = Number(quantity);
        if (!Number.isFinite(q) || q < 0.001) {
          return { cart: state.cart.filter((item) => item.lineId !== lineId) };
        }
        return {
          cart: state.cart.map((item) => (item.lineId === lineId ? { ...item, quantity: q } : item)),
        };
      }
      const q = Math.floor(Number(quantity));
      if (!Number.isFinite(q) || q < 1) {
        return { cart: state.cart.filter((item) => item.lineId !== lineId) };
      }
      return {
        cart: state.cart.map((item) => (item.lineId === lineId ? { ...item, quantity: q } : item)),
      };
    }),
  removeCartLine: (lineId) =>
    set((state) => ({
      cart: state.cart.filter((item) => item.lineId !== lineId),
    })),
  setCartLineNote: (lineId, note) =>
    set((state) => ({
      cart: state.cart.map((item) =>
        item.lineId === lineId
          ? { ...item, lineNote: note?.trim() || null }
          : item
      ),
    })),
  updateCartLineMenuOptions: (lineId, menuOptions, lineNote) =>
    set((state) => ({
      cart: state.cart.map((item) => {
        if (item.lineId !== lineId) return item;
        const basePrice = item.price - optionsPriceDelta(item.menuOptions);
        const newPrice = unitPriceWithOptions(basePrice, menuOptions);
        return {
          ...item,
          menuOptions: menuOptions?.length ? menuOptions : undefined,
          price: newPrice,
          lineNote: lineNote != null ? (lineNote.trim() || null) : item.lineNote,
        };
      }),
    })),
  updateCartLineVariant: (lineId, variantId, name, price, sku) =>
    set((state) => ({
      cart: state.cart.map((item) =>
        item.lineId === lineId
          ? {
              ...item,
              variantId,
              name,
              price,
              sku: sku ?? item.sku,
            }
          : item
      ),
    })),
  updateCustomCartLine: (lineId, name, amount) =>
    set((state) => ({
      cart: state.cart.map((item) =>
        item.lineId === lineId && item.itemType === 'custom'
          ? {
              ...item,
              name: name.trim() || 'Venta Varios',
              price: amount,
            }
          : item
      ),
    })),
  setCartLinePrice: (lineId, price) =>
    set((state) => ({
      cart: state.cart.map((item) =>
        item.lineId === lineId ? { ...item, price } : item
      ),
    })),
  clearCart: () => set({ cart: [] }),
  replaceCart: (cart) => set({ cart }),
  setCartLineAttribution: (lineId, performedByUserId, soldByUserId) =>
    set((state) => ({
      cart: state.cart.map((item) =>
        item.lineId === lineId
          ? { ...item, performedByUserId, soldByUserId }
          : item
      ),
    })),
}));
